'use client';

import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type JobFilterPillProps = {
  name: string;
  label: string;
  anyLabel: string;
  value: string;
  options: { value: string; label: string }[];
};

/** Filter menu for the GET search form. Picking a value submits the form. */
export function JobFilterPill({
  name,
  label,
  anyLabel,
  value,
  options,
}: JobFilterPillProps) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const pick = (next: string) => {
    setCurrent(next);
    setOpen(false);
    if (!inputRef.current) return;
    // The form reads the input directly, before React renders the new value.
    inputRef.current.value = next;
    inputRef.current.form?.requestSubmit();
  };

  const selected = options.find((option) => option.value === current);
  const items = [{ value: '', label: anyLabel }, ...options];

  return (
    <div ref={rootRef} className="relative shrink-0">
      <input ref={inputRef} type="hidden" name={name} defaultValue={value} />
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-label={selected ? `${label}: ${selected.label}` : label}
        onClick={() => setOpen(!open)}
        className={`font-matter focus-visible:outline-status-live inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border px-4 text-[14px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${
          selected
            ? 'border-brand-primary/60 bg-surface-brand text-status-live'
            : 'border-border-soft text-text-secondary hover:text-text-primary hover:border-text-secondary'
        }`}
      >
        {selected?.label ?? label}
        <ChevronDown aria-hidden size={14} strokeWidth={2} />
      </button>
      {open && (
        <ul className="border-border-soft bg-surface-card absolute start-0 top-[52px] z-20 min-w-[200px] rounded-[16px] border p-1.5 shadow-[0_24px_60px_rgba(0,0,0,0.28)]">
          {items.map((item) => (
            <li key={item.value}>
              <button
                type="button"
                aria-pressed={item.value === current}
                onClick={() => pick(item.value)}
                className={`font-matter hover:bg-surface-subtle focus-visible:outline-status-live flex min-h-11 w-full cursor-pointer items-center rounded-[10px] px-3.5 text-start text-[14px] whitespace-nowrap focus-visible:outline-2 ${
                  item.value === current
                    ? 'text-status-live'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
